import { skipToken } from "@reduxjs/toolkit/query";
import EntityMember from "_shared/entity-members/entity-member";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import Individual from "marketplace/individuals/individual";
import { useMemo } from "react";

/**
 * The directory's profile for each of `members`, keyed by user id, so a handover can name the
 * people it is about. Somebody the directory has no profile for is simply absent from the map, and
 * the caller names them generically.
 */
export default function useTeamIndividuals(members: readonly (EntityMember | undefined)[]): Map<string, Individual> {
  // The ids are joined into one string so the query argument is only new when the people are: the
  // members array is rebuilt on every render by its callers. A deliberate stability guarantee.
  const idsKey = members
    .filter((member): member is EntityMember => member !== undefined)
    .map((member) => member.userId.value)
    .sort()
    .join(",");
  const userIds = useMemo(() => new Set(idsKey ? idsKey.split(",") : []), [idsKey]);
  const query = useGetIndividualsByUserIdsQuery(userIds.size ? { userIds: [...userIds] } : skipToken);

  return useMemo(() => {
    const individuals = query.data ? (Individual.fromJSONArray(query.data) ?? []) : [];
    return new Map(individuals.map((individual) => [individual.userId.value, individual]));
  }, [query.data]);
}

/** What to call `member` in a sentence: their name, or "a team member" when nobody knows it. */
export function nameOf(individuals: Map<string, Individual>, member: EntityMember | undefined): string {
  if (!member) return "a team member";
  return individuals.get(member.userId.value)?.fullName || "a team member";
}
